/** @jsx jsx */
import { css, jsx } from "@emotion/core"
import React from "react"
import { Icon, Image, Spacer, Table } from "@cd2/components"
import { format } from "date-fns/esm"
import { Link } from "react-router-dom"
import { IComponentProp, titleize } from "@cd2/tools"
import { ISort } from "../Components/Tables/utils"
import { icons } from "../constants/icons"
import { FilePreview, useFileboy } from "@cd2/fileboy-react"

interface IThProps {
  children: React.ReactNode
  sorted?: "ASC" | "DESC"

  onClick(): void
}

export function Th({ children, onClick, sorted }: IThProps) {
  return (
    <div
      css={css`
        cursor: pointer;
        user-select: none;

        &:hover {
          color: #777;
        }
      `}
      onClick={onClick}
    >
      <span>{children}</span>
      <Spacer horizontal />
      {sorted && <Icon icon={sorted === "ASC" ? icons.chevronUp : icons.chevronDown} />}
    </div>
  )
}

export function FileboyImageColumn<T>({
  field,
}: {
  field: string | ((row: T) => string | undefined)
}) {
  const fileboy = useFileboy()
  const table = Table.useTable()
  if (table.isHeading) {
    return (
      <th
        css={css`
          padding: 2px;
        `}
      ></th>
    )
  }
  const row = table.row
  const fileboyId = typeof field === "string" ? table.row[field] : field(row as any)
  const url = fileboyId
    ? fileboy.buildImageUrl(fileboyId, { transforms: [{ type: "resize", width: 60 }] })
    : ""

  return (
    <td
      css={css`
        overflow: hidden;
        padding: 2px;
        width: 50px;
        height: 50px;
      `}
    >
      <Image.Background
        src={url}
        css={css`
          width: 48px;
          height: 48px;
        `}
        contain
      />
    </td>
  )
}

export function FileboyFilePreviewColumn<T>({
  field,
}: {
  field: string | ((row: T) => string | undefined)
}) {
  const table = Table.useTable()

  const getFileboyId = () => {
    if (table.isHeading) {
      return
    }
    const row = table.row
    const fileboyId = typeof field === "string" ? row[field] : field(row as any)
    return fileboyId
  }

  if (table.isHeading) {
    return (
      <th
        css={css`
          padding: 2px;
        `}
      ></th>
    )
  }

  const fileboyId = getFileboyId()

  return (
    <td
      css={css`
        overflow: hidden;
        padding: 2px;
        width: 44px;
        height: 44px;

        & > div {
          padding: 0px;
        }
      `}
    >
      {fileboyId && <FilePreview id={fileboyId} height={44} />}
    </td>
  )
}

export function ColumnHasOneAssociation({
  path = "",
  onSortClick,
  sort,
  heading,
  children,
}: {
  path: string
  onSortClick(attr: string): void
  sort: ISort
  heading?: string
  linkTo?: string
  children?(row: any): React.ReactNode
}) {
  const [assoc, ...attrs] = path.split(".")
  const isSorted = sort && path === sort.by

  function traverse(values: any[], obj: Record<string, any>) {
    let temp = obj
    for (const v of values) {
      temp = temp[v]
      if (!temp) {
        break
      }
    }
    return temp
  }

  return (
    <Table.Column
      heading={
        <Th onClick={() => onSortClick(path)} sorted={isSorted ? sort.direction : undefined}>
          {heading || titleize(`${assoc} ${attrs.join(" ")}`)}
        </Th>
      }
    >
      {row => (children ? children(row) : row[assoc] ? traverse(attrs, row[assoc]) : undefined)}
    </Table.Column>
  )
}

interface IColumnSortableProps {
  field: string
  sort: ISort
  heading?: string
  render?: IComponentProp<typeof Table.DataColumn, "render">
  children?: IComponentProp<typeof Table.Column, "children">

  onSortClick(attr: string): void
}

export function ColumnSortable({
  sort,
  heading,
  field,
  onSortClick,
  render,
  children,
}: IColumnSortableProps) {
  const renderHeading = (
    <Th onClick={() => onSortClick(field)} sorted={sort.by === field ? sort.direction : undefined}>
      {heading || titleize(field)}
    </Th>
  )
  if (children) {
    return <Table.Column children={children} heading={renderHeading} />
  }
  return <Table.DataColumn field={field} render={render} heading={renderHeading} />
}

interface IColumnUpdatedAtProps {
  sort: ISort

  onSortClick(attr: string): void
}

export function ColumnUpdatedAt({ sort, onSortClick }: IColumnUpdatedAtProps) {
  return (
    <Table.Column
      heading={
        <Th
          onClick={() => onSortClick("updated_at")}
          sorted={sort.by === "updated_at" ? sort.direction : undefined}
        >
          Last Edit
        </Th>
      }
    >
      {row => (
        <React.Fragment>
          {format(new Date(row.updated_at), `dd/MM/yyyy`)}
          <br />
          {format(new Date(row.updated_at), `HH:mm`)}
        </React.Fragment>
      )}
    </Table.Column>
  )
}

export function ColumnSageNumber({ path = [] }: { path?: string[] }) {
  function getStatus(row: any) {
    let value = row
    path.forEach(segment => {
      value = row[segment]
    })
    if (!value) {
      return null
    }
    let status: any = null
    if (value?.sage_order_number) {
      status = <div>{value?.sage_order_number}</div>
    } else if (value?.processed) {
      status = <div>Manually processed</div>
    } else if (value?.sage_export_status === "pending") {
      status = <div style={{ color: "grey" }}>Exporting...</div>
    } else {
      status = <div style={{ color: "grey" }}>{titleize(value?.sage_export_status || "")}</div>
    }
    return status
  }

  return <Table.Column heading="Sage No.">{row => getStatus(row)}</Table.Column>
}

export function TableLink({ to, children }: { to: string; children: React.ReactNode }) {
  return (
    <span>
      {children}
      <Spacer size={8} horizontal />
      <Link
        css={css`
          padding: 5px 8px 7px 8px;
          margin: -5px -8px -7px -8px;
          transition: 0.1s;
          border-radius: 8px;
          background: rgba(0, 0, 0, 0);

          &:hover {
            background: rgba(0, 0, 0, 0.1);
          }
        `}
        onClick={e => e.stopPropagation()}
        to={to}
      >
        <Icon icon={icons.external} size={10} />
      </Link>
    </span>
  )
}
